import { Component } from 'react';
import { isStaleChunk, reloadOnceForStaleChunk } from '@/lib/staleChunk';
import { Button } from '@/ui/Button';

/**
 * Last line of defence. A render error anywhere below this shows a recovery
 * screen instead of React unmounting the tree and leaving a blank page.
 * The raw error goes to the console only, never onto the screen.
 */
export class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { error: null, reloading: false };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, errorInfo) {
    // Wire this to your error tracker when one is chosen.
    console.error('Unhandled render error', error, errorInfo);
    if (isStaleChunk(error) && reloadOnceForStaleChunk()) this.setState({ reloading: true });
  }

  handleReload = () => {
    this.setState({ error: null });
    window.location.reload();
  };

  render() {
    const { error } = this.state;
    // Reloading for an out-of-date page file: show nothing rather than an
    // error screen that flashes for the instant before the page reloads.
    if (this.state.reloading) return null;
    if (!error) return this.props.children;

    return (
      <div className="flex min-h-screen items-center justify-center bg-surface-muted p-6">
        <div className="w-full max-w-md rounded-card bg-surface p-8 text-center shadow-card">
          <h1 className="text-lg font-semibold text-content">Something went wrong</h1>
          <p className="mt-2 text-sm text-content-muted">
            The page failed to load. Reloading usually fixes it.
          </p>

          <Button className="mt-6" fullWidth onClick={this.handleReload}>
            Reload page
          </Button>
        </div>
      </div>
    );
  }
}
